import React, { useState, useEffect, useRef } from 'react';
import { Scan, Plus, X, Download, AlertCircle, CheckCircle, Eye, EyeOff, RefreshCw, ChevronDown, ChevronRight } from 'lucide-react';
import PageIdentifier from '../shared/PageIdentifier';
import SCREEN_IDS from '../../utils/screenIds';
import usePermissions from '../../hooks/usePermissions';
import * as XLSX from 'xlsx';
import { todayCentral } from '../../shared/utils/dateUtils';

const ExhibitionReconciliation = () => {
  const { hasPermission } = usePermissions();
  const canDelete = hasPermission('exhibition-reconciliation', 'delete');
  const [activeTab, setActiveTab] = useState('setup'); // setup, opening, closing, reconcile
  const [exhibitionName, setExhibitionName] = useState('');
  const [exhibitionDate, setExhibitionDate] = useState(todayCentral());
  const [openingItems, setOpeningItems] = useState([]);
  const [closingItems, setClosingItems] = useState([]);
  const [soldItems, setSoldItems] = useState([]);
  const [reconciliation, setReconciliation] = useState(null);
  const [scanInput, setScanInput] = useState('');
  const [showExpanded, setShowExpanded] = useState({});
  const [loading, setLoading] = useState(false);
  const [exhibitionStartDate, setExhibitionStartDate] = useState(todayCentral());
  const [exhibitionEndDate, setExhibitionEndDate] = useState(todayCentral());
  const [autoLoadedSales, setAutoLoadedSales] = useState(false);
  const [dupMsg, setDupMsg] = useState('');         // inline duplicate warning (non-blocking)
  const dupMsgTimerRef = React.useRef(null);

  const showDupMsg = (msg) => {
    if (dupMsgTimerRef.current) clearTimeout(dupMsgTimerRef.current);
    setDupMsg(msg);
    dupMsgTimerRef.current = setTimeout(() => setDupMsg(''), 2500);
  };

  // ── Backend session state ─────────────────────────────────────────────────
  const [sessionId, setSessionId] = useState(null);
  const [sessionsList, setSessionsList] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [showSessions, setShowSessions] = useState(true);
  const [showCompletedSessions, setShowCompletedSessions] = useState(false);
  const [viewingCompleted, setViewingCompleted] = useState(null); // completed session detail
  const [lastSynced, setLastSynced] = useState(null);
  const pollIntervalRef = useRef(null);
  const sessionIdRef = useRef(null); // keep ref in sync for use inside interval

  // Keep ref in sync
  useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);

  // ── Polling: sync items from backend every 12 seconds when session is active ──
  const pollSession = async (id) => {
    if (!id) return;
    try {
      const res = await fetch(`/api/reconciliation/sessions/${id}`, { credentials: 'include' });
      const data = await res.json();
      if (!data.success) return;
      const d = data.data;
      const toItem = i => ({ ...i, code: i.item_code || i.code, name: i.item_name || i.name, scannedAt: i.scannedAt || new Date().toLocaleTimeString() });
      // Only update if count changed — avoids re-render flicker when nothing changed
      setOpeningItems(prev => {
        const next = (d.opening_items || []).map(toItem);
        return next.length !== prev.length ? next : prev;
      });
      setClosingItems(prev => {
        const next = (d.closing_items || []).map(toItem);
        return next.length !== prev.length ? next : prev;
      });
      setSoldItems(prev => {
        const next = (d.sold_items || []).map(toItem);
        return next.length !== prev.length ? next : prev;
      });
      setLastSynced(new Date());
    } catch (e) {
      // silently ignore poll errors — offline or transient
    }
  };

  // Start/stop polling when sessionId changes
  useEffect(() => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    if (sessionId) {
      pollIntervalRef.current = setInterval(() => pollSession(sessionIdRef.current), 12000);
    }
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [sessionId]);

  // Load active sessions on mount
  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    setSessionsLoading(true);
    try {
      const res = await fetch('/api/reconciliation/sessions', { credentials: 'include' });
      const data = await res.json();
      if (data.success) setSessionsList(data.data);
    } catch (e) {
      console.warn('Could not load sessions:', e);
    } finally {
      setSessionsLoading(false);
    }
  };

  const ensureSession = async () => {
    if (sessionId) return sessionId;
    const name = exhibitionName.trim() || `Exhibition ${new Date().toLocaleDateString()}`;
    const res = await fetch('/api/reconciliation/sessions', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, start_date: exhibitionStartDate, end_date: exhibitionEndDate }),
    });
    const data = await res.json();
    if (!data.success) throw new Error('Failed to create session');
    setSessionId(data.data.id);
    setExhibitionName(name);
    loadSessions();
    return data.data.id;
  };

  const saveItemToBackend = async (sessId, tabType, item) => {
    try {
      await fetch(`/api/reconciliation/sessions/${sessId}/items`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tab_type: tabType,
          item_code: item.code,
          item_name: item.name,
          item_data: item,
          auto_loaded: item.autoLoaded || false,
        }),
      });
    } catch (e) {
      console.warn('Backend save skipped:', e.message);
    }
  };

  const resumeSession = async (sess) => {
    try {
      setSessionsLoading(true);
      const res = await fetch(`/api/reconciliation/sessions/${sess.id}`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) {
        const d = data.data;
        setSessionId(d.id);
        setExhibitionName(d.name);
        setExhibitionStartDate(d.start_date?.split('T')[0] || exhibitionStartDate);
        setExhibitionEndDate(d.end_date?.split('T')[0] || exhibitionEndDate);
        const toItem = i => ({ ...i, code: i.item_code || i.code, name: i.item_name || i.name, scannedAt: i.scannedAt || new Date().toLocaleTimeString() });
        setOpeningItems((d.opening_items || []).map(toItem));
        setClosingItems((d.closing_items || []).map(toItem));
        setSoldItems((d.sold_items || []).map(toItem));
        if (d.reconciliation_result) setReconciliation(d.reconciliation_result);
        setActiveTab('opening');
        setShowSessions(false);
      }
    } catch (e) {
      alert('Failed to load session');
    } finally {
      setSessionsLoading(false);
    }
  };

  // Auto-load sold items from billings when exhibition dates are set
  const handleAutoLoadSoldItems = async () => {
    if (!exhibitionStartDate || !exhibitionEndDate) {
      alert('Please set both start and end dates');
      return;
    }

    try {
      setLoading(true);
      // Fetch billings for the date range
      const response = await fetch(
        `/api/billings?startDate=${exhibitionStartDate}&endDate=${exhibitionEndDate}T23:59:59`,
        { credentials: 'include' }
      );

      if (!response.ok) {
        alert('Failed to fetch billings');
        return;
      }

      const responseData = await response.json();
      const billings = responseData.data;

      if (!Array.isArray(billings) || billings.length === 0) {
        alert('No billings found for this date range');
        return;
      }

      // Extract unique item codes — each billing row has item_code directly
      const soldItemCodes = [];
      billings.forEach(bill => {
        if (bill.item_code && !soldItemCodes.includes(bill.item_code)) {
          soldItemCodes.push(bill.item_code);
        }
      });

      if (soldItemCodes.length === 0) {
        alert('No items found in billings for this date range');
        return;
      }

      // Fetch details for each item code
      const newSoldItems = [];
      for (const code of soldItemCodes) {
        try {
          const itemResponse = await fetch(`/api/jewelry/details/${code}`, {
            credentials: 'include'
          });

          if (itemResponse.ok) {
            const item = await itemResponse.json();
            const itemWithTimestamp = {
              ...item,
              scannedAt: new Date().toLocaleTimeString(),
              scannedDate: new Date().toLocaleDateString(),
              autoLoaded: true
            };
            newSoldItems.push(itemWithTimestamp);
          }
        } catch (error) {
          console.error(`Error fetching item ${code}:`, error);
        }
      }

      setSoldItems(newSoldItems);
      setAutoLoadedSales(true);

      // Bulk-save to backend
      try {
        const sessId = await ensureSession();
        await fetch(`/api/reconciliation/sessions/${sessId}/items?tab_type=sold`, { method: 'DELETE', credentials: 'include' });
        if (newSoldItems.length > 0) {
          await fetch(`/api/reconciliation/sessions/${sessId}/items`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: newSoldItems.map(item => ({
                tab_type: 'sold', item_code: item.code, item_name: item.name, item_data: item, auto_loaded: true,
              })),
            }),
          });
        }
      } catch (e) {
        console.warn('Backend bulk save skipped:', e.message);
      }

      alert(`Auto-loaded ${newSoldItems.length} items from billings`);
    } catch (error) {
      console.error('Error auto-loading sold items:', error);
      alert('Error fetching billings data');
    } finally {
      setLoading(false);
    }
  };

  // Add item by code
  const handleAddItem = async (code, type) => {
    if (!code.trim()) {
      alert('Please enter an item code');
      return;
    }

    const normalizedCode = code.trim().toUpperCase();

    // Duplicate check — prevent same code in the same tab
    const listForType =
      type === 'opening' ? openingItems :
        type === 'closing' ? closingItems :
          soldItems;

    if (listForType.find(i => (i.code || '').toUpperCase() === normalizedCode)) {
      showDupMsg(`"${normalizedCode}" is already in the ${type} list.`);
      setScanInput('');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`/api/jewelry/details/${normalizedCode}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        alert(`Item "${normalizedCode}" not found in inventory.`);
        setScanInput('');
        return;
      }

      const item = await response.json();

      const itemWithTimestamp = {
        ...item,
        scannedAt: new Date().toLocaleTimeString(),
        scannedDate: new Date().toLocaleDateString()
      };

      if (type === 'opening') {
        setOpeningItems([...openingItems, itemWithTimestamp]);
      } else if (type === 'closing') {
        setClosingItems([...closingItems, itemWithTimestamp]);
      } else if (type === 'sold') {
        setSoldItems([...soldItems, itemWithTimestamp]);
      }

      setScanInput('');

      // Persist to backend
      try {
        const sessId = await ensureSession();
        await saveItemToBackend(sessId, type, itemWithTimestamp);
      } catch (e) {
        console.warn('Backend save skipped:', e.message);
      }
    } catch (error) {
      console.error('Error fetching item:', error);
      alert('Error fetching item details');
    } finally {
      setLoading(false);
    }
  };

  // Group items by code for display (handles duplicates from multi-user polling)
  const groupItems = (list) => {
    const map = {};
    list.forEach((item) => {
      const key = (item.code || '').toUpperCase();
      if (!map[key]) map[key] = { ...item, count: 1 };
      else map[key].count++;
    });
    return Object.values(map);
  };

  // Remove all entries for a given code from the list
  const removeItemByCode = async (code, type) => {
    if (!canDelete) return;
    const normalizedCode = (code || '').toUpperCase();
    const list = type === 'opening' ? openingItems : type === 'closing' ? closingItems : soldItems;
    const toRemove = list.filter(i => (i.code || '').toUpperCase() === normalizedCode);

    const filterFn = (i) => (i.code || '').toUpperCase() !== normalizedCode;
    if (type === 'opening') setOpeningItems(openingItems.filter(filterFn));
    else if (type === 'closing') setClosingItems(closingItems.filter(filterFn));
    else if (type === 'sold') setSoldItems(soldItems.filter(filterFn));

    // Remove all matching backend rows
    if (sessionId) {
      for (const item of toRemove) {
        if (item?.id) {
          try {
            await fetch(`/api/reconciliation/sessions/${sessionId}/items/${item.id}`, {
              method: 'DELETE',
              credentials: 'include',
            });
          } catch (e) {
            console.warn('Backend item delete skipped:', e.message);
          }
        }
      }
    }
  };

  // Perform reconciliation
  const performReconciliation = () => {
    const openingCount = openingItems.length;
    const closingCount = closingItems.length;
    const soldCount = soldItems.length;

    // Calculate expected count
    const expectedCount = closingCount + soldCount;
    const discrepancy = openingCount - expectedCount;

    // Find missing items
    const closingCodes = closingItems.map(item => item.code);
    const soldCodes = soldItems.map(item => item.code);
    const accountedCodes = new Set([...closingCodes, ...soldCodes]);

    const missingItems = openingItems.filter(item => !accountedCodes.has(item.code));

    const result = {
      openingCount,
      closingCount,
      soldCount,
      expectedCount,
      discrepancy,
      discrepancyPercentage: openingCount > 0 ? ((discrepancy / openingCount) * 100).toFixed(2) : 0,
      status: discrepancy === 0 ? 'matched' : discrepancy > 0 ? 'missing' : 'extra',
      missingItems,
      timestamp: new Date().toLocaleString()
    };

    setReconciliation(result);

    // If we have a backend session, call the reconcile endpoint too
    if (sessionId) {
      fetch(`/api/reconciliation/sessions/${sessionId}/reconcile`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      }).then(() => loadSessions()).catch(e => console.warn('Backend reconcile skipped:', e.message));
    }
  };

  // Export reconciliation report
  const exportReport = () => {
    if (!reconciliation) return;

    const reportData = [
      ['RECONCILIATION REPORT'],
      [],
      ['Exhibition Name:', exhibitionName || 'N/A'],
      ['Date:', exhibitionDate],
      ['Reconciliation Time:', reconciliation.timestamp],
      [],
      ['SUMMARY'],
      ['Opening Items:', reconciliation.openingCount],
      ['Sold Items:', reconciliation.soldCount],
      ['Closing Items:', reconciliation.closingCount],
      ['Expected Items:', reconciliation.expectedCount],
      ['Discrepancy:', reconciliation.discrepancy],
      ['Discrepancy %:', `${reconciliation.discrepancyPercentage}%`],
      ['Status:', reconciliation.status.toUpperCase()],
      [],
      ['OPENING ITEMS'],
      ['Code', 'Name', 'Category', 'Vendor', 'Weight', 'Status']
    ];

    openingItems.forEach(item => {
      reportData.push([
        item.code,
        item.name,
        item.category_name,
        item.vendor_code || 'N/A',
        item.net_weight || 'N/A',
        item.status
      ]);
    });

    reportData.push([]);
    reportData.push(['CLOSING ITEMS']);
    reportData.push(['Code', 'Name', 'Category', 'Vendor', 'Weight', 'Status']);

    closingItems.forEach(item => {
      reportData.push([
        item.code,
        item.name,
        item.category_name,
        item.vendor_code || 'N/A',
        item.net_weight || 'N/A',
        item.status
      ]);
    });

    reportData.push([]);
    reportData.push(['SOLD ITEMS']);
    reportData.push(['Code', 'Name', 'Category', 'Vendor', 'Weight', 'Status']);

    soldItems.forEach(item => {
      reportData.push([
        item.code,
        item.name,
        item.category_name,
        item.vendor_code || 'N/A',
        item.net_weight || 'N/A',
        item.status
      ]);
    });

    if (reconciliation.missingItems.length > 0) {
      reportData.push([]);
      reportData.push(['MISSING ITEMS (NOT FOUND IN CLOSING OR SOLD)']);
      reportData.push(['Code', 'Name', 'Category', 'Vendor', 'Weight']);

      reconciliation.missingItems.forEach(item => {
        reportData.push([
          item.code,
          item.name,
          item.category_name,
          item.vendor_code || 'N/A',
          item.net_weight || 'N/A'
        ]);
      });
    }

    const worksheet = XLSX.utils.aoa_to_sheet(reportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Reconciliation');

    const filename = `Exhibition_Reconciliation_${exhibitionDate}_${exhibitionName.replace(/\s+/g, '_')}.xlsx`;
    XLSX.writeFile(workbook, filename);
  };

  // Render item card (item may have .count from groupItems)
  const ItemCard = ({ item, type }) => (
    <div className="bg-white border border-gray-200 rounded-lg p-3 mb-2 flex items-center justify-between hover:shadow-md transition-shadow">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-gray-800">{item.code}</span>
          {item.count > 1 && (
            <span className="bg-amber-100 text-amber-700 text-xs font-bold px-1.5 py-0.5 rounded-full">×{item.count}</span>
          )}
          {item.autoLoaded && (
            <span className="bg-green-100 text-green-700 text-xs px-1.5 py-0.5 rounded-full">Auto</span>
          )}
        </div>
        <div className="text-xs text-gray-600 truncate">{item.name}</div>
        <div className="text-xs text-gray-500 mt-1">
          {item.category_name} • {item.vendor_code || 'N/A'} • {item.net_weight}g
        </div>
        <div className="text-xs text-gray-400 mt-1">{item.scannedAt}</div>
      </div>
      {canDelete && (
        <button
          onClick={() => removeItemByCode(item.code, type)}
          className="ml-2 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
        >
          <X size={18} />
        </button>
      )}
    </div>
  );

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <PageIdentifier screenId={SCREEN_IDS.UTILITIES.EXHIBITION_RECONCILIATION} />

      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
            <Scan className="text-blue-600" size={32} />
            Reconciliation
          </h1>
        </div>

        {/* Exhibition Details */}
        {activeTab === 'setup' && (
          <div className="space-y-6">

            {/* ── Active Session Banner ─────────────────────────────────── */}
            {sessionId && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-blue-700">📡 Session #{sessionId} — {exhibitionName}</span>
                  {lastSynced && (
                    <span className="text-xs text-blue-500">
                      ↻ synced {lastSynced.toLocaleTimeString()}
                    </span>
                  )}
                </div>
                <button onClick={() => { setSessionId(null); setExhibitionName(''); setOpeningItems([]); setClosingItems([]); setSoldItems([]); setReconciliation(null); loadSessions(); }} className="text-xs text-red-600 font-semibold hover:underline ml-4">
                  ✕ Disconnect
                </button>
              </div>
            )}

            {/* ── Resume Existing Sessions ──────────────────────────────── */}
            {!sessionId && (
              <>
                {/* ── Active Sessions ── */}
                <div className="bg-white rounded-lg shadow-md p-4">
                  <button onClick={() => setShowSessions(s => !s)} className="flex items-center gap-2 w-full text-left font-semibold text-gray-700 text-sm">
                    {showSessions ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    📂 Active Sessions
                    {sessionsLoading && <RefreshCw size={13} className="animate-spin ml-2 text-gray-400" />}
                    <span className="ml-auto text-xs text-gray-400">
                      {sessionsList.filter(s => s.status === 'active').length} active
                    </span>
                  </button>
                  {showSessions && (
                    <div className="mt-3 space-y-2">
                      {sessionsList.filter(s => s.status === 'active').length === 0 ? (
                        <p className="text-xs text-gray-400">No active sessions</p>
                      ) : sessionsList.filter(s => s.status === 'active').map(sess => (
                        <button key={sess.id} onClick={() => resumeSession(sess)}
                          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg border text-left transition-colors border-green-200 bg-green-50 hover:bg-green-100">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-sm text-gray-900">{sess.name}</span>
                              <span className="text-xs px-1.5 py-0.5 rounded-full font-medium bg-green-200 text-green-800">Active</span>
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5">
                              {sess.start_date?.split('T')[0]} → {sess.end_date?.split('T')[0]}
                              {' · '}📦 {sess.opening_count || 0} · 📥 {sess.closing_count || 0} · 💰 {sess.sold_count || 0}
                            </div>
                          </div>
                          <span className="text-xs text-green-600 font-medium flex-shrink-0">Resume →</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* ── Completed Sessions ── */}
                <div className="bg-white rounded-lg shadow-md p-4">
                  <button onClick={() => setShowCompletedSessions(s => !s)} className="flex items-center gap-2 w-full text-left font-semibold text-gray-700 text-sm">
                    {showCompletedSessions ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    ✅ Completed Sessions
                    <span className="ml-auto text-xs text-gray-400">
                      {sessionsList.filter(s => s.status !== 'active').length} completed
                    </span>
                  </button>
                  {showCompletedSessions && (
                    <div className="mt-3 space-y-2">
                      {sessionsList.filter(s => s.status !== 'active').length === 0 ? (
                        <p className="text-xs text-gray-400">No completed sessions yet</p>
                      ) : sessionsList.filter(s => s.status !== 'active').map(sess => (
                        <div key={sess.id} className="rounded-lg border border-gray-200 bg-gray-50">
                          <button onClick={() => setViewingCompleted(viewingCompleted?.id === sess.id ? null : sess)}
                            className="w-full flex items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-gray-100 rounded-lg">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-sm text-gray-900">{sess.name}</span>
                                <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                                  sess.status === 'completed' ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'
                                }`}>
                                  {sess.status === 'completed' ? 'Completed' : 'Cancelled'}
                                </span>
                                {sess.reconciliation_result && (
                                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                                    sess.reconciliation_result.status === 'MATCHED' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                                  }`}>
                                    {sess.reconciliation_result.status}
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                {sess.start_date?.split('T')[0]} → {sess.end_date?.split('T')[0]}
                                {' · '}📦 {sess.opening_count || 0} · 📥 {sess.closing_count || 0} · 💰 {sess.sold_count || 0}
                                {sess.reconciled_at && ` · Reconciled: ${new Date(sess.reconciled_at).toLocaleDateString()}`}
                              </div>
                            </div>
                            <span className="text-xs text-gray-500 flex-shrink-0">
                              {viewingCompleted?.id === sess.id ? '▲ Hide' : '▼ View'}
                            </span>
                          </button>

                          {/* Inline result panel */}
                          {viewingCompleted?.id === sess.id && sess.reconciliation_result && (
                            <div className="px-3 pb-3">
                              <div className={`rounded-lg p-4 mt-1 border ${
                                sess.reconciliation_result.status === 'MATCHED'
                                  ? 'bg-green-50 border-green-200'
                                  : 'bg-orange-50 border-orange-200'
                              }`}>
                                <p className={`text-sm font-bold mb-3 ${
                                  sess.reconciliation_result.status === 'MATCHED' ? 'text-green-700' : 'text-orange-700'
                                }`}>
                                  {sess.reconciliation_result.status === 'MATCHED' ? '✓ Reconciliation Matched' : `⚠ Discrepancy: ${sess.reconciliation_result.discrepancy} items`}
                                </p>
                                <div className="grid grid-cols-3 gap-2 text-center text-xs mb-3">
                                  <div className="bg-white rounded p-2 border">
                                    <div className="font-bold text-gray-800 text-lg">{sess.reconciliation_result.openingCount}</div>
                                    <div className="text-gray-500">Opening</div>
                                  </div>
                                  <div className="bg-white rounded p-2 border">
                                    <div className="font-bold text-gray-800 text-lg">{sess.reconciliation_result.closingCount}</div>
                                    <div className="text-gray-500">Closing</div>
                                  </div>
                                  <div className="bg-white rounded p-2 border">
                                    <div className="font-bold text-gray-800 text-lg">{sess.reconciliation_result.soldCount}</div>
                                    <div className="text-gray-500">Sold</div>
                                  </div>
                                </div>
                                {sess.reconciliation_result.missingItems?.length > 0 && (
                                  <div>
                                    <p className="text-xs font-semibold text-red-700 mb-1">Missing Items ({sess.reconciliation_result.missingItems.length}):</p>
                                    <div className="space-y-1 max-h-32 overflow-y-auto">
                                      {sess.reconciliation_result.missingItems.map((item, idx) => (
                                        <div key={idx} className="text-xs bg-white rounded px-2 py-1 border border-red-100 text-red-700">
                                          {item.code} {item.name ? `— ${item.name}` : ''}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {sess.reconciliation_result.reconciledBy && (
                                  <p className="text-xs text-gray-500 mt-2">
                                    By {sess.reconciliation_result.reconciledBy} · {sess.reconciliation_result.timestamp ? new Date(sess.reconciliation_result.timestamp).toLocaleString() : ''}
                                  </p>
                                )}
                              </div>
                            </div>
                          )}
                          {viewingCompleted?.id === sess.id && !sess.reconciliation_result && (
                            <div className="px-3 pb-3">
                              <p className="text-xs text-gray-400 italic">No reconciliation result stored for this session.</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Exhibition Details</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Exhibition Name
                  </label>
                  <input
                    type="text"
                    value={exhibitionName}
                    onChange={(e) => setExhibitionName(e.target.value)}
                    placeholder="e.g., Annual Jewelry Expo 2026"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Exhibition Date
                  </label>
                  <input
                    type="date"
                    value={exhibitionDate}
                    onChange={(e) => setExhibitionDate(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <button
                  onClick={() => setActiveTab('opening')}
                  className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Start - Scan Opening Items
                </button>
              </div>
            </div>

          </div>
        )}

        {/* Tabs */}
        {activeTab !== 'setup' && (
          <div className="flex gap-2 mb-6 overflow-x-auto">
            <button
              onClick={() => { setActiveTab('setup'); setShowSessions(true); }}
              className="px-3 py-2 rounded-lg font-medium transition-colors bg-white text-gray-600 border border-gray-300 hover:bg-gray-50 flex items-center gap-1 text-sm whitespace-nowrap"
              title="Back to sessions list"
            >
              ← Sessions
            </button>
            {[
              { id: 'opening', label: '📥 Opening' },
              { id: 'closing', label: '📤 Closing' },
              { id: 'sold', label: '💰 Sold' },
              { id: 'reconcile', label: '✓ Reconcile' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${activeTab === tab.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* Opening Items Tab */}
        {activeTab === 'opening' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center justify-between">
                Scan Opening Items
                {lastSynced && <span className="text-xs font-normal text-green-600">↻ {lastSynced.toLocaleTimeString()}</span>}
              </h2>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Item Code
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={scanInput}
                      onChange={(e) => setScanInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleAddItem(scanInput, 'opening');
                        }
                      }}
                      placeholder="Scan or type item code (e.g., DR-1)"
                      autoFocus
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <button
                      onClick={() => handleAddItem(scanInput, 'opening')}
                      disabled={loading}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
                    >
                      <Plus size={20} />
                    </button>
                  </div>
                  {dupMsg && (
                    <div className="flex items-center gap-2 mt-2 px-3 py-2 bg-amber-50 border border-amber-300 rounded-lg text-sm text-amber-800 font-medium">
                      <span>⚠️</span> {dupMsg}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">
                  Items Scanned: {openingItems.length}
                </h3>
                {openingItems.length > 0 && (
                  <button
                    onClick={() => setShowExpanded({ ...showExpanded, opening: !showExpanded.opening })}
                    className="text-blue-600 hover:text-blue-700"
                  >
                    {showExpanded.opening ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                )}
              </div>
              {showExpanded.opening && (
                <div className="max-h-96 overflow-y-auto space-y-2">
                  {groupItems(openingItems).map((item, idx) => (
                    <ItemCard key={idx} item={item} type="opening" />
                  ))}
                </div>
              )}
              {openingItems.length > 0 && (
                <button
                  onClick={() => setActiveTab('closing')}
                  className="w-full mt-4 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition-colors font-medium"
                >
                  Next - Scan Closing Items
                </button>
              )}
            </div>
          </div>
        )}

        {/* Closing Items Tab */}
        {activeTab === 'closing' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center justify-between">
                Scan Closing Items
                {lastSynced && <span className="text-xs font-normal text-green-600">↻ {lastSynced.toLocaleTimeString()}</span>}
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                Scan items that are still in stock at end of day
              </p>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Item Code
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={scanInput}
                      onChange={(e) => setScanInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleAddItem(scanInput, 'closing');
                        }
                      }}
                      placeholder="Scan or type item code"
                      autoFocus
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <button
                      onClick={() => handleAddItem(scanInput, 'closing')}
                      disabled={loading}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
                    >
                      <Plus size={20} />
                    </button>
                  </div>
                  {dupMsg && (
                    <div className="flex items-center gap-2 mt-2 px-3 py-2 bg-amber-50 border border-amber-300 rounded-lg text-sm text-amber-800 font-medium">
                      <span>⚠️</span> {dupMsg}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">
                  Items Scanned: {closingItems.length}
                </h3>
                {closingItems.length > 0 && (
                  <button
                    onClick={() => setShowExpanded({ ...showExpanded, closing: !showExpanded.closing })}
                    className="text-blue-600 hover:text-blue-700"
                  >
                    {showExpanded.closing ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                )}
              </div>
              {showExpanded.closing && (
                <div className="max-h-96 overflow-y-auto space-y-2">
                  {groupItems(closingItems).map((item, idx) => (
                    <ItemCard key={idx} item={item} type="closing" />
                  ))}
                </div>
              )}

              <button
                onClick={async () => { await handleAutoLoadSoldItems(); setActiveTab('sold'); }}
                disabled={loading}
                className="w-full mt-4 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors font-medium flex items-center justify-center gap-2"
              >
                {loading && <RefreshCw size={14} className="animate-spin" />}
                Next - Scan Sold Items
              </button>
            </div>
          </div>
        )}

        {/* Sold Items Tab */}
        {activeTab === 'sold' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center justify-between">
                Add/Scan Sold Items
                {lastSynced && <span className="text-xs font-normal text-green-600">↻ {lastSynced.toLocaleTimeString()}</span>}
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                {autoLoadedSales ? (
                  <span className="text-green-600">✓ {soldItems.length} items auto-loaded from billings. You can add more items below.</span>
                ) : (
                  'Scan items that were sold during the exhibition'
                )}
              </p>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Item Code
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={scanInput}
                      onChange={(e) => setScanInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleAddItem(scanInput, 'sold');
                        }
                      }}
                      placeholder="Scan or type item code"
                      autoFocus
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <button
                      onClick={() => handleAddItem(scanInput, 'sold')}
                      disabled={loading}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
                    >
                      <Plus size={20} />
                    </button>
                  </div>
                  {dupMsg && (
                    <div className="flex items-center gap-2 mt-2 px-3 py-2 bg-amber-50 border border-amber-300 rounded-lg text-sm text-amber-800 font-medium">
                      <span>⚠️</span> {dupMsg}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">
                  Items Scanned: {soldItems.length}
                </h3>
                {soldItems.length > 0 && (
                  <button
                    onClick={() => setShowExpanded({ ...showExpanded, sold: !showExpanded.sold })}
                    className="text-blue-600 hover:text-blue-700"
                  >
                    {showExpanded.sold ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                )}
              </div>
              {showExpanded.sold && (
                <div className="max-h-96 overflow-y-auto space-y-2">
                  {groupItems(soldItems).map((item, idx) => (
                    <ItemCard key={idx} item={item} type="sold" />
                  ))}
                </div>
              )}
              <button
                onClick={() => performReconciliation()}
                className="w-full mt-4 bg-purple-600 text-white py-2 rounded-lg hover:bg-purple-700 transition-colors font-medium"
              >
                Complete & Reconcile
              </button>
            </div>
          </div>
        )}

        {/* Reconciliation Results */}
        {activeTab === 'reconcile' && reconciliation && (
          <div className="space-y-6">
            {/* Summary Card */}
            <div className={`rounded-lg shadow-md p-6 ${reconciliation.status === 'matched' ? 'bg-green-50 border-2 border-green-300' :
                reconciliation.status === 'missing' ? 'bg-red-50 border-2 border-red-300' :
                  'bg-yellow-50 border-2 border-yellow-300'
              }`}>
              <div className="flex items-center gap-3 mb-4">
                {reconciliation.status === 'matched' ? (
                  <CheckCircle size={32} className="text-green-600" />
                ) : (
                  <AlertCircle size={32} className="text-red-600" />
                )}
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">
                    {reconciliation.status === 'matched' ? '✓ Reconciliation Matched!' : '⚠ Discrepancy Found'}
                  </h2>
                  <p className="text-sm text-gray-600">{reconciliation.timestamp}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="text-sm font-medium text-gray-600">Opening Items</div>
                  <div className="text-3xl font-bold text-gray-800">{reconciliation.openingCount}</div>
                </div>
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="text-sm font-medium text-gray-600">Closing Items</div>
                  <div className="text-3xl font-bold text-gray-800">{reconciliation.closingCount}</div>
                </div>
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="text-sm font-medium text-gray-600">Sold Items</div>
                  <div className="text-3xl font-bold text-gray-800">{reconciliation.soldCount}</div>
                </div>
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="text-sm font-medium text-gray-600">Expected</div>
                  <div className="text-3xl font-bold text-gray-800">{reconciliation.expectedCount}</div>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="text-sm font-medium text-gray-600 mb-1">Discrepancy</div>
                  <div className="text-2xl font-bold text-red-600">
                    {reconciliation.discrepancy > 0 ? '+' : ''}{reconciliation.discrepancy}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {reconciliation.discrepancyPercentage}% of opening
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="text-sm font-medium text-gray-600 mb-1">Status</div>
                  <div className={`text-2xl font-bold ${reconciliation.status === 'matched' ? 'text-green-600' : 'text-red-600'
                    }`}>
                    {reconciliation.status === 'matched' ? 'MATCHED' : 'MISSING'}
                  </div>
                </div>
              </div>
            </div>

            {/* Missing Items */}
            {reconciliation.missingItems.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-red-500">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <AlertCircle size={20} className="text-red-600" />
                  Missing Items ({reconciliation.missingItems.length})
                </h3>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {reconciliation.missingItems.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-red-50 p-3 rounded-lg border border-red-200">
                      <div>
                        <div className="font-semibold text-gray-800">{item.code}</div>
                        <div className="text-sm text-gray-600">{item.name}</div>
                        <div className="text-xs text-gray-500">
                          {item.category_name} • {item.vendor_code || 'N/A'} • {item.net_weight}g
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-2 justify-between">
              <button
                onClick={() => {
                  setActiveTab('setup');
                  setOpeningItems([]);
                  setClosingItems([]);
                  setSoldItems([]);
                  setReconciliation(null);
                  setExhibitionName('');
                  setExhibitionDate(todayCentral());
                  setSessionId(null);
                  loadSessions();
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
              >
                Start New Reconciliation
              </button>
              <button
                onClick={exportReport}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center gap-2"
              >
                <Download size={18} />
                Export Report
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExhibitionReconciliation;
